import { useCallback } from 'react'
import { useParams } from 'react-router'

import Text from '@/elements/Text'
import BackLink from '@/elements/BackLink'
import Flex from '@/elements/Flex'
import {
  AlertCategories,
  AlertComments,
  AlertDetails,
  AlertStatus
} from '@/components/Alert'
import Loading from '@/elements/Loading'
import useBreakpoints from '@/hooks/useBreakpoints'
import useRoles from '@/hooks/useRoles'
import toast from '@/utils/toast'
import { useGetUser } from '@/api/user'
import { useGetPrivateSubject } from '@/api/profile'
import { updateAlert, useGetAlert, useGetAlertDelegates } from '@/api/alert'

function Alert() {
  const { isDelegate, isAdmin, isSubject } = useRoles()
  const currentUserCanEdit = isDelegate || isAdmin || isSubject

  const { isMobile, isTablet } = useBreakpoints()
  const { id } = useParams<{ id: string; }>()

  const { data: alertData, isLoading, refetch } = useGetAlert(id)
  const alert = alertData?.data?.alert

  const { data: delegatesData, isLoading: delegatesLoading } = useGetAlertDelegates(id)
  const delegates = delegatesData?.data

  const { data: userData } = useGetPrivateSubject(alert?.subject?.id, !!alert?.subject?.id)
  const user = userData?.data

  const { data: assignedToData } = useGetUser(alert?.assignedTo || undefined)
  const assignedUser = assignedToData?.data?.user
  const { data: escalatedToData } = useGetUser(alert?.escalateTo || undefined)
  const escalatedUser = escalatedToData?.data?.user

  const updateAlertData = useCallback(async (newData: any) => {
    if (!id) return
    try {
      await updateAlert(id, newData)
      refetch()
      toast.success('Alert updated successfully!')
    } catch {
      toast.error('Failed to update alert')
    }
  }, [id, refetch])

  const renderStatusContent = () => (
    <Flex
      className='card'
      wfull
      p={!isMobile ? 4 : 3}
      gap={5}
    >
      <Text variant='h3'>Status</Text>
      {alert?.status &&
        <AlertStatus
          status={alert.status}
          assignedUser={assignedUser}
          escalatedUser={escalatedUser}
          updateStatus={updateAlertData}
          delegates={delegates || []}
          disabled={!currentUserCanEdit}
        />
      }
    </Flex>
  )

  const renderAlertCategories = () => (
    <Flex
      className='card'
      wfull
      hfull
      p={!isMobile ? 4 : 3}
      gap={5}
    >
      <AlertCategories
        alertCategoriesData={
          {
            notMe: alert?.alertDetails?.notMe,
            authorized: alert?.alertDetails?.authorized,
            threat: alert?.alertDetails?.threat,
          }
        }
        updateAlert={updateAlertData}
      />
    </Flex>
  )

  return (
    <Flex component='section' gap={4} grow>
      {isLoading || delegatesLoading ? (
        <Loading />
      ) : (
        <>
          <BackLink />
          <Text variant='h2'>{alert?.alertDetails.title}</Text>
          <Flex inline wfull wrap gap={3}>
            <Flex fraction={6.4} gap={3} style={{ maxWidth: '100%' }}>
              <Flex wfull className='card' p={!isMobile ? 4 : 3} gap={5}>
                <Text variant='h3'>Issue Details</Text>
                <AlertDetails
                  userData={user}
                  alertData={alert}
                />
              </Flex>
              {isTablet && (
                <>
                  {renderAlertCategories()}
                  {renderStatusContent()}
                </>
              )}
              <Flex wfull className='card' p={!isMobile ? 4 : 3} gap={5}>
                <Text variant='h3'>Comments & Activity</Text>
                {alert && (
                  <AlertComments
                    alertId={alert.id}
                    activity={alert?.activity || []}
                    disabled={!currentUserCanEdit}
                  />
                )}
              </Flex>
            </Flex>
            {!isTablet && (
              <Flex
                wfull
                fraction={3.5}
                gap={3}
                style={{
                  position: !isTablet ? 'sticky' : 'static',
                  top: !isTablet ? '-2rem' : '0'
                }}
              >
                {renderAlertCategories()}
                {renderStatusContent()}
              </Flex>
            )}
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default Alert
